<template>
  <form ref="$form" novalidate @submit.prevent="onSubmit">
    <input type="hidden" name="form_id" :value="content.formId" />
    <input type="hidden" name="form_type" :value="content.type" />
    <input type="hidden" name="referrer" :value="navigation.referrer" />
    <!-- <Feedback :content="message" class="form-field" /> -->

    <VInput
      v-model="form.firstName"
      :label="t('form-firstname')"
      name="firstname"
      class="form-field"
    />
    <VInput
      v-model="form.lastName"
      :label="t('form-lastname')"
      name="lastname"
      class="form-field"
    />
    <VInput
      v-model="form.email"
      :label="t('form-email')"
      name="email"
      type="email"
      class="form-field"
    />
    <VInput
      v-model="form.phone"
      name="phone"
      :label="t('form-phone')"
      class="form-field"
    />
    <VInput
      v-model="form.portfolio"
      :required="false"
      :label="t('form-portfolio')"
      name="portfolio"
      class="form-field"
    />

    <div class="form-field row radio-buttons">
      <div class="type form-field--inline col-xxs-12 col-xs-6" data-input>
        <span class="form-title">{{ t('form-jobs-type') }}</span>
        <VRadio
          v-for="type in data.type"
          :id="type.value"
          :key="type.value"
          v-model="form.type"
          :required="true"
          name="type"
          :value="type.value"
          :label="type.label"
          class="form-field--small"
        />
        <p class="input__error">{{ t('form-invalid') }}</p>
      </div>
      <div class="form-field--inline col-xxs-12 col-xs-6" data-input>
        <span class="form-title">{{ t('form-jobs-job') }}</span>
        <VRadio
          v-for="job in data.job"
          :id="job.value"
          :key="job.value"
          v-model="form.job"
          :required="true"
          name="job"
          :value="job.value"
          :label="job.label"
          class="form-field--small"
        />
        <p class="input__error">{{ t('form-invalid') }}</p>
      </div>
    </div>

    <VTextarea
      id="textarea"
      v-model="form.message"
      name="description"
      :label="t('form-description')"
      class="form-field"
    />

    <Upload
      ref="uploader"
      class="form-field"
      :upload-url="content.uploadUrl"
      @file-added="onFileAdded"
    />

    <!-- eslint-disable  -->
    <VCheckbox
      v-model="form.newsLetter"
      id="newsletter"
      name="newsletter"
      :required="false"
      :label="t('form-newsletter')"
      class="form-field"
    />
    <!-- eslint-enable  -->
    <Feedback v-if="hasError" :content="feedback" class="form-field" />
    <div class="turnstile-widget"></div>

    <div class="row end-xxs">
      <GBtn :disabled="isLoading" type="submit">{{ send }}</GBtn>
    </div>
  </form>
</template>

<script setup lang="ts">
import { useAxios } from '@monofront/vue-axios-plugin'
import { computed, reactive, ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'

import Feedback from '@/components/ui/Feedback.vue'
import Upload from '@/components/ui/Upload.vue'
import VCheckbox from '@/components/form/Checkbox.vue'
import VInput from '@/components/form/Input.vue'
import VRadio from '@/components/form/Radio.vue'
import VTextarea from '@/components/form/Textarea.vue'
import {
  validateForm,
  preFillField,
  getTurnstileToken,
  removeTurnstile,
  stringifyFormDataNewsletter,
} from '@/modules/form'
import { localePathFromName } from '@/router/i18n-router'
import { useChromeStore } from '@/stores/chrome'
import { useNavigationStore } from '@/stores/navigation'
import { logger } from '@/utils/logger'
import { pushForm, formStart } from '@/utils/tracking'

const props = defineProps({
  content: {
    type: Object,
    required: true,
  },
})

const axios = useAxios()
const navigation = useNavigationStore()
const { t, ...chrome } = useChromeStore()

const feedback = reactive({
  type: 'warning',
  text: t('form-error'),
})

const data = reactive({
  type: [
    {
      label: 'An internship',
      value: 'An internship',
    },
    {
      label: 'A part time job',
      value: 'A part time job',
    },
    {
      label: 'A full time job',
      value: 'A full time job',
    },
  ],

  job: [
    {
      label: 'Account Manager',
      value: 'Account Manager',
    },
    {
      label: 'Project Manager',
      value: 'Project Manager',
    },
    {
      label: 'Office Manager',
      value: 'Office Manager',
    },
    {
      label: 'Backend Developer',
      value: 'Backend Developer',
    },
    {
      label: 'Frontend Developer',
      value: 'Frontend Developer',
    },
    {
      label: 'UI Designer',
      value: 'UI Designer',
    },
    {
      label: 'UX Designer',
      value: 'UX Designer',
    },
    {
      label: 'Other',
      value: 'Other',
    },
  ],
})

const form = reactive({
  firstName: '',
  lastName: '',
  portfolio: '',
  email: '',
  message: '',
  phone: '',
  type: '',
  job: '',
  newsLetter: false,
})

const router = useRouter()
const uploader = ref<InstanceType<typeof Upload>>()
const $form = ref<HTMLFormElement>({} as HTMLFormElement)
const isLoading = ref(false)
const hasError = ref(false)
const fileList = ref<string[]>([])

const send = computed(() =>
  isLoading.value ? t('form-sending') : t('form-send')
)

const onFileAdded = (fileId: string) => {
  fileList.value.push(fileId)
}

onMounted(() => {
  formStart($form.value)
  preFillField(form)
})

const onSubmit = async () => {
  pushForm('click')

  const isValid = validateForm($form.value)

  if (!isValid || isLoading.value) {
    return
  }

  isLoading.value = true
  hasError.value = false
  const formData = new FormData($form.value)
  formData.append('tags', 'website,contact-form,candidats')
  let turnstileWidgetID: string

  try {
    const { id, token } = await getTurnstileToken()

    if (token) {
      turnstileWidgetID = id
      formData.append('cf-turnstile-response', token)
    }
  } catch (error) {
    console.error(error)
  }

  if (fileList.value.length) {
    fileList.value.forEach(file => {
      formData.append('uploads[]', file)
    })
  }

  stringifyFormDataNewsletter(formData)

  axios
    .post(
      `${import.meta.env.VITE_API_DOMAIN + props.content.submitUrl}`,
      formData
    )
    .catch(res => {
      hasError.value = true
      pushForm('error')
      logger.error(res)
      isLoading.value = false

      return res
    })
    .then(res => {
      if (res.data?.status === 200) {
        uploader.value?.filePond?.destroy()
        pushForm('success')
        pushForm('success', formData)

        router.push({
          name: `thank-you-${chrome.language}`,
          query: {
            type: props.content.type,
          },
          params: {
            segment: chrome.language === 'fr' ? 'merci' : 'thank-you',
          },
        })
      } else {
        hasError.value = true
        pushForm('error')
        logger.error(res)
        isLoading.value = false
      }
    })
    .finally(() => {
      removeTurnstile(turnstileWidgetID)
    })
}
</script>

<style lang="scss" scoped>
.type {
  @include mq(xs) {
    margin-bottom: 0;
  }
}

.radio-buttons {
  :deep(sup) {
    display: none;
  }
}
</style>
